import React, { useState } from 'react';
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  Space,
  message,
  Row,
  Col,
  Avatar,
  Divider,
  Alert
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  SaveOutlined,
  KeyOutlined
} from '@ant-design/icons';
import { apiService } from '../../../services/api';
import { uiConfig } from '../../../config/ui';

const { Title, Text } = Typography;

interface PasswordFormData {
  old_password: string;
  new_password: string;
  confirm_password: string;
}

const Profile: React.FC = () => {
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordForm] = Form.useForm();

  const handlePasswordChange = async (values: PasswordFormData) => {
    try {
      setPasswordLoading(true);
      
      if (values.new_password !== values.confirm_password) {
        message.error('新密码和确认密码不一致');
        return;
      }

      const response = await apiService.adminChangePassword({
        old_password: values.old_password,
        new_password: values.new_password,
      });

      if (response.code === 0) {
        message.success('密码修改成功，请重新登录');
        passwordForm.resetFields();
        
        // 清除token并跳转到登录页
        setTimeout(() => {
          localStorage.removeItem('admin_token');
          window.location.href = '/admin/login';
        }, uiConfig.animation.duration);
      } else {
        message.error(response.message || '密码修改失败');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || '密码修改失败');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Title level={2} style={{ margin: 0 }}>
            <Space>
              <UserOutlined />
              个人资料
            </Space>
          </Title>
          <Text type="secondary">
            管理您的账户信息和安全设置
          </Text>
        </Col>

        {/* 基本信息 */}
        <Col xs={24} lg={12}>
          <Card title="基本信息">
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Avatar size={80} icon={<UserOutlined />} />
              <div style={{ marginTop: 16 }}>
                <Title level={4} style={{ margin: 0 }}>
                  管理员
                </Title>
                <Text type="secondary">系统管理员</Text>
              </div>
            </div>

            <Divider />

            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>用户名：</Text>
                <Text>admin</Text>
              </div>
              <div>
                <Text strong>角色：</Text>
                <Text>系统管理员</Text>
              </div>
              <div>
                <Text strong>最后登录：</Text>
                <Text>{new Date().toLocaleString()}</Text>
              </div>
            </Space>
          </Card>
        </Col>

        {/* 修改密码 */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <LockOutlined />
                修改密码
              </Space>
            }
          >
            <Alert
              message="安全提示"
              description="为了账户安全，建议定期更换密码。新密码应包含字母、数字，长度至少6位。"
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={handlePasswordChange}
              autoComplete="off"
            >
              <Form.Item
                name="old_password"
                label="当前密码"
                rules={[
                  { required: true, message: '请输入当前密码' },
                ]}
              >
                <Input.Password
                  placeholder="请输入当前密码"
                  prefix={<KeyOutlined />}
                />
              </Form.Item>

              <Form.Item
                name="new_password"
                label="新密码"
                rules={[
                  { required: true, message: '请输入新密码' },
                  { min: 6, message: '密码长度至少6位' },
                  { max: 20, message: '密码长度最多20位' },
                ]}
              >
                <Input.Password
                  placeholder="请输入新密码"
                  prefix={<LockOutlined />}
                />
              </Form.Item>

              <Form.Item
                name="confirm_password"
                label="确认新密码"
                dependencies={['new_password']}
                rules={[
                  { required: true, message: '请确认新密码' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('new_password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('两次输入的密码不一致'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  placeholder="请再次输入新密码"
                  prefix={<LockOutlined />}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={passwordLoading}
                  icon={<SaveOutlined />}
                  block
                >
                  修改密码
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Profile;
